import React, { useRef, useState } from "react"
import Button from "@mui/material/Button"
import FormControl from "@mui/material/FormControl"
import TextField from "@mui/material/TextField"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { useForm } from "react-hook-form"
import { useSnackbar } from "notistack"
import Editor from "@monaco-editor/react"
import { verifyResponse } from "utils/httpRequest"
import { addSystemConfigs, editSystemConfigs } from "services/systemConfig"
import { StyledSystemConfigDialog } from "./SystemConfig.style"

const SystemConfigDlg = (props) => {
  const { enqueueSnackbar } = useSnackbar()
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      resourceId: null,
    },
  })

  const initSystemConfig = {
    id: 0,
    name: "",
    configKey: "",
    configValue: "",
    description: "",
  }

  const { onSubmit } = props
  const [isEditMode, setIsEditMode] = useState(false)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const systemConfigRef = useRef(initSystemConfig)

  const openDlg = (editSystemConfig) => {
    reset()
    if (editSystemConfig) {
      systemConfigRef.current = editSystemConfig
      setValue("name", editSystemConfig.name || "")
      setValue("configKey", editSystemConfig.configKey || "")
      setValue("description", editSystemConfig.description || "")
      setValue("configValue", editSystemConfig.configValue || "{}")
      setIsEditMode(true)
    } else {
      systemConfigRef.current = initSystemConfig
      setValue("name", "")
      setValue("configKey", "")
      setValue("description", "")
      setValue("configValue", "{}")
      setIsEditMode(false)
    }
    setOpen(true)
  }

  const createNewSystemConfig = async ({
    name,
    configKey,
    configValue,
    description,
  }) => {
    setLoading(true)
    try {
      const response = await addSystemConfigs({
        name,
        configKey,
        configValue,
        description,
      })
      verifyResponse(response)
      if (response.rtnCode === 200) {
        enqueueSnackbar("System config created", { variant: "success" })
        onSubmit && (await onSubmit())
        setOpen(false)
      } else {
        enqueueSnackbar("Failed to create system config", { variant: "warning" })
      }
    } catch {
      enqueueSnackbar("Failed to create system config", { variant: "warning" })
    } finally {
      setLoading(false)
    }
  }

  const updateSystemConfig = async ({
    name,
    configKey,
    configValue,
    description,
    id,
  }) => {
    setLoading(true)
    try {
      const response = await editSystemConfigs({
        name,
        configKey,
        configValue,
        description,
        id,
      })
      verifyResponse(response)
      if (response.rtnCode === 200) {
        enqueueSnackbar("System config updated", { variant: "success" })
        onSubmit && (await onSubmit())
        setOpen(false)
      } else {
        enqueueSnackbar("Failed to update system config", { variant: "warning" })
      }
    } catch {
      enqueueSnackbar("Failed to update system config", { variant: "warning" })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleAction = async (data) => {
    if (isEditMode) {
      await updateSystemConfig({ ...data, id: systemConfigRef.current.id })
      return
    }
    await createNewSystemConfig(data)
  }

  const dlg = (
    <StyledSystemConfigDialog open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit(handleAction)}>
        <DialogTitle>
          <div className="dialog-title-group">
            <h2 className="dialog-title">
              {isEditMode ? "Edit System Config" : "Add System Config"}
            </h2>
            <p className="dialog-subtitle">
              Configure key metadata and the backing JSON payload.
            </p>
          </div>
        </DialogTitle>
        <DialogContent>
          <FormControl>
            <TextField
              autoFocus
              autoComplete="off"
              id="system-config-name"
              label="Name"
              type="text"
              fullWidth
              defaultValue={systemConfigRef.current.name || ""}
              {...register("name", { required: true })}
              variant="outlined"
              size="small"
              error={!!errors?.name}
              helperText={!!errors?.name ? "* Config name is required" : " "}
            />
          </FormControl>

          <FormControl>
            <TextField
              autoComplete="off"
              id="system-config-key"
              label="Config Key"
              type="text"
              fullWidth
              defaultValue={systemConfigRef.current.configKey || ""}
              {...register("configKey", { required: true })}
              variant="outlined"
              size="small"
              error={!!errors?.configKey}
              helperText={!!errors?.configKey ? "* Config key is required" : " "}
            />
          </FormControl>

          <FormControl>
            <TextField
              label="Description"
              id="system-config-description"
              fullWidth
              variant="outlined"
              size="small"
              rows={3}
              multiline
              defaultValue={systemConfigRef.current.description || ""}
              {...register("description", { required: true })}
              error={!!errors?.description}
              helperText={
                !!errors?.description ? "* Description is required" : " "
              }
            />
          </FormControl>

          <div className="label-text">Json Value</div>

          <div className="error-message">
            {!!errors?.configValue ? "Wrong Json Format" : ""}
          </div>
          <Editor
            className="json-editor"
            height="350px"
            theme="vs-dark"
            defaultLanguage="json"
            value={watch("configValue") || "{}"}
            onChange={(newValue) => {
              setValue("configValue", newValue || "{}", {
                shouldValidate: true,
              })
            }}
            options={{
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
            }}
          />
          <input
            type="hidden"
            defaultValue={systemConfigRef.current.configValue || "{}"}
            {...register("configValue", {
              validate: (value) => {
                try {
                  const parsedObj = JSON.parse(value)
                  void parsedObj
                  return true
                } catch {
                  return false
                }
              },
            })}
          />
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-cancel-btn"
            variant="outlined"
            size="medium"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            className="dialog-submit-btn"
            type="submit"
            variant="contained"
            size="medium"
            disabled={loading}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </StyledSystemConfigDialog>
  )

  return [openDlg, dlg, loading]
}

export default SystemConfigDlg
